import {
  getEnvironment,
  getGtmContainerId,
  getSentryDsn,
  getSentryRelease,
} from "$utils/env/env";
import type { ErrorResponseMessage } from "@crown/data";
import { handlers as ga4, init as ga4Init } from "@crown/ga4";
import "@crown/jewels/client";
import { init } from "@crown/jewels/client";
import { LoggableError, addErrorListener } from "@crown/logging";
import { logToConsole } from "@crown/logging/src/log-to-console";
import { addHandlers, sendToServer } from "@crown/tracking";
import { registerImageScalers } from "./image-scalers";
import "./styles/fonts.css";

const dsn = getSentryDsn();

// On checkout we deliberately DON'T discard "network-ish" errors. On iOS Safari a
// Ledyer/Klarna SDK script that fails to load, or a payment redirect that gets
// aborted/backgrounded, surfaces as exactly "Load failed" / "Failed to fetch" /
// "avbruten" — the very failures behind lost orders. Dropping them everywhere (as
// before) hid device-specific checkout breakage. Off checkout they're still noise.
function isCheckoutRoute(): boolean {
  return typeof window !== "undefined" && /checkout/i.test(window.location.pathname);
}

// Recover from stale dynamic-import chunks after a deploy — the #1 cause of
// "Importing a module script failed" (NODE-7), and it hits in-app WebViews (FB/IG)
// hardest. Vite fires `vite:preloadError` when a code-split chunk fails to load;
// reload once to pull the current build's manifest. Guarded via sessionStorage so a
// genuinely-broken chunk can't loop — after a recent reload we let the error surface
// to Sentry instead. (Durable follow-up: retain old chunks across deploys.)
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    // Never hard-reload mid-SCA/BankID — it would strand the payment. Let the
    // error surface instead; the widget recovers or the shopper retries.
    if (document.body.classList.contains("checkout-locked-sca")) return;
    const KEY = "vite-preload-reloaded-at";
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < 10_000) return; // reloaded just now → don't loop
    sessionStorage.setItem(KEY, String(Date.now()));
    event.preventDefault(); // we're recovering; suppress the unhandled throw
    window.location.reload();
  });
}

import("@sentry/browser").then((SentryBrowser) => {
  SentryBrowser.init({
    dsn,
    environment: getEnvironment(),
    release: getSentryRelease() || undefined,
    tracesSampleRate: 0.1,
    // Replaces the blanket ignoreErrors: keep network errors on checkout (tagged),
    // drop them elsewhere. Sentry auto-captures os/browser/device, so checkout
    // failures can then be grouped by device to confirm a browser-specific cause.
    beforeSend(event, hint) {
      const onCheckout = isCheckoutRoute();
      if (onCheckout) {
        event.tags = { ...event.tags, area: "checkout" };
      }
      // Drop errors injected by in-app browsers. Meta's Instagram/Facebook WKWebViews
      // inject a native bridge (`sendDataToNative` → `window.webkit.messageHandlers`) that
      // throws in that context. It's not our code (those symbols exist nowhere in this repo)
      // and isn't actionable — pure noise on every IG/FB in-app page load. See Sentry NODE-9.
      if (looksLikeInjectedThirdPartyError(hint?.originalException)) {
        return null;
      }
      if (looksLikeBrowserNoise(hint?.originalException, event)) {
        return null;
      }
      if (looksLikeNetworkError(hint?.originalException) && !onCheckout) {
        return null;
      }
      // "Not logged in" is an expected condition, not a bug: an auth-gated resource
      // (logo library, company collection, …) is fetched while the session isn't resolved
      // (dropped/expired cookie, or the page simply isn't logged in). It surfaces as an
      // unhandled rejection that bypasses the crown logging bus' ignoreCodes, so drop it here —
      // off checkout, mirroring the network-noise handling above. See NODE-1B / NODE-T.
      if (looksLikeAuthError(hint?.originalException) && !onCheckout) {
        return null;
      }
      return event;
    },
  });

  addErrorListener(logErrorClient);

  function logErrorClient(error: string | LoggableError) {
    // Bare string messages on the Crown logging bus are debug/info logs, NOT errors
    // (e.g. "Storing doc in cache …", "Got prefetched document …", "Loaded … Transforming").
    // Capturing them flooded Sentry with non-error issues and buried the real ones — so we
    // no longer send strings as events (they still appear as breadcrumbs via logToConsole).
    // Only genuine exceptions (LoggableError) become issues.
    if (typeof error !== "string" && (isWorthLogging(error) || isCheckoutRoute())) {
      const code = (error as ErrorResponseMessage).code;

      // A userMessage means the server classified this as an expected, handled,
      // user-facing condition (validation, duplicate registration, wrong file
      // type). Track the frequency at warning level; don't count it as an error.
      const isHandledUserError = !!(error as { userMessage?: string }).userMessage;

      SentryBrowser.withScope((scope: any) => {
        if (code) {
          // group errors together based on their code
          scope.setFingerprint([code]);
        }
        if (isHandledUserError) {
          scope.setLevel("warning");
          SentryBrowser.captureMessage(
            `${code || "userError"}: ${(error as Error).message}`
          );
        } else {
          SentryBrowser.captureException(error);
        }
      });
    }
  }
});

// Errors from native-bridge code injected by in-app browsers (Meta Instagram/Facebook),
// on both platforms — none of these symbols exist in our code:
//   iOS WKWebView:   window.webkit.messageHandlers / sendDataToNative
//     "messageHandlers" covers Safari ("evaluating 'window.webkit.messageHandlers'")
//     and Chrome ("reading 'messageHandlers'").
//   Android WebView: the `iabjs://` bridge — "Java object is gone" when the injected
//     @JavascriptInterface object is GC'd on unload, plus its instrumentation calls.
// Browser/environment noise with no action for us: the benign ResizeObserver
// warning, aborted navigations/fetches (user navigated away mid-load), and
// Outlook SafeLink scanners probing pages ("Object Not Found Matching Id").
function looksLikeBrowserNoise(err: unknown, event?: { message?: string }): boolean {
  const message =
    (err && typeof err === "object" && "message" in err
      ? String((err as { message?: unknown }).message ?? "")
      : String(err ?? "")) ||
    event?.message ||
    "";
  return (
    message.includes("ResizeObserver loop") ||
    message.includes("signal is aborted") ||
    message.includes("Fetch is aborted") ||
    message.includes("Object Not Found Matching Id")
  );
}

function looksLikeInjectedThirdPartyError(err: unknown): boolean {
  const message =
    (err && typeof err === "object" && "message" in err
      ? String((err as { message?: unknown }).message ?? "")
      : String(err ?? "")) || "";
  return (
    message.includes("messageHandlers") ||
    message.includes("sendDataToNative") ||
    message.includes("Java object is gone") ||
    message.includes("iabjs") ||
    message.includes("enableDidUserTypeOnKeyboardLogging")
  );
}

function looksLikeNetworkError(err: unknown): boolean {
  const message =
    (err && typeof err === "object" && "message" in err
      ? String((err as { message?: unknown }).message ?? "")
      : String(err ?? "")) || "";
  return (
    message.includes("Failed to fetch") ||
    message.includes("getaddrinfo ENOTFOUND") ||
    message.includes("NetworkError") ||
    message.includes("nedkopplad") ||
    message === "Not found" ||
    message.includes("Load failed") ||
    message.includes("avbruten")
  );
}

// An auth-required failure: the server responded notLoggedIn (401) because the request hit an
// auth-gated resource without a resolved session. responseMessageToError copies `code`/`status`/
// `userMessage` onto the Error, so match on those (and the Swedish userMessage as a backstop for
// any layer that dropped the code).
function looksLikeAuthError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { code?: string; userMessage?: string; message?: string };
  return (
    e.code === "notLoggedIn" ||
    e.userMessage === "Du måste vara inloggad." ||
    (typeof e.message === "string" && e.message.includes("Du måste vara inloggad"))
  );
}

addErrorListener(logToConsole);

const ignoreCodes = ["notLoggedIn"];

export function isWorthLogging(error: ErrorResponseMessage) {
  const message = error.message || "";

  const isNetworkProblem =
    ignoreCodes.includes(error.code || "") ||
    /* chrome */
    message.includes("Failed to fetch") ||
    message.includes("getaddrinfo ENOTFOUND") ||
    /* firefox */
    message.includes("NetworkError") ||
    /* safari */
    message.includes("nedkopplad") ||
    message == "Not found" ||
    /* mobile safari */
    message.includes("Load failed") ||
    message.includes("avbruten");

  return !isNetworkProblem;
}

const containerId = getGtmContainerId();

if (containerId && getEnvironment() !== "development") {
  ga4Init({ containerId, debugMode: getEnvironment() == "staging" });
  addHandlers(ga4);
}

// Bot user agents excluded from first-party tracking (#249). A JS-executing crawler would
// otherwise fire the tracking bus on every page it renders; skipping registration here means no
// /resources/track POSTs happen for them at all (cheaper than the server rejecting each), and keeps
// bot traffic out of the analytics numbers. Duplicates bot-user-agent.ts on purpose — this runs in
// the client entry and shouldn't pull in a shared module.
function isBotUserAgent(userAgent: string): boolean {
  return [
    "Googlebot",
    "Storebot-Google",
    "AdsBot",
    "Bot/",
    "bot/",
    "bingbot",
    "okhttp",
    "Acunetix",
    "crawler",
    "scrapy",
    "spider",
    "python-requests",
    "headless",
    "HeadlessChrome",
    "meta-external",
    "AhrefsSiteAudit",
    "Facebot",
    "facebookexternalhit",
    "Twitterbot",
    "serpstatbot",
    // Naver
    "Yeti",
    // note that Klarna uses this
    "Java/",
    // ancient browser doing way too many requests — must be a bot
    "Pixel 2",
    "SM-G900P Build/LRX21T",
    "Nexus 5",
  ].some((bot) => userAgent.includes(bot));
}

// First-party storefront event tracking (#249). Forwards an allowlist of the already-fired
// tracking-bus events to our own CommerceHub ingest via the shop proxy (/resources/track), which
// mints the anonymous session id and strips PII. Fires independent of the marketing-consent banner
// that gates GA4 above — legitimate-interest, PII-free (see the #249 consent decision). Skipped for
// bot user agents so crawlers don't inflate the numbers.
if (getEnvironment() !== "development" && !isBotUserAgent(navigator.userAgent)) {
  addHandlers({
    pageViewed: sendToServer,
    productViewed: sendToServer,
    categoryPageViewed: sendToServer,
    addedToCart: sendToServer,
    removedFromCart: sendToServer,
    checkoutStepOpened: sendToServer,
    checkoutStepSubmitted: sendToServer,
    purchased: sendToServer,
  });
}

registerImageScalers();

init(new Map());
